import {
  Banknote,
  Building2,
  CalendarClock,
  type LucideIcon,
  MapPin,
  Ruler,
  Tag,
} from "lucide-react";

/** Single source of truth for the editable property fields, grouped into the sections
 *  shown on the Details tab. Each section renders as its own inline-edit card
 *  (PropertySectionCard). `impact` is the data→effect cue shown under a field in edit
 *  mode so the user sees what the value drives downstream. */

export type FieldKind = "text" | "number" | "date" | "money" | "select" | "landlord";

export interface PropertyField {
  key: string;
  label: string;
  kind: FieldKind;
  /** Lookup category for kind === "select". */
  category?: string;
  /** One-line data→effect cue, shown under the input in edit mode. */
  impact?: string;
}

export interface PropertySection {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  fields: PropertyField[];
}

export const PROPERTY_SECTIONS: PropertySection[] = [
  {
    id: "location",
    title: "Location",
    description: "Where the property is — used on listings and documents.",
    icon: MapPin,
    fields: [
      { key: "reference", label: "Reference", kind: "text" },
      { key: "street", label: "Street", kind: "text" },
      { key: "house_number", label: "House number", kind: "text" },
      { key: "house_number_suffix", label: "Suffix", kind: "text" },
      { key: "postal_code", label: "Postal code", kind: "text" },
      { key: "city", label: "City", kind: "text" },
      { key: "city_area", label: "Area / district", kind: "text" },
    ],
  },
  {
    id: "ownership",
    title: "Ownership",
    description: "Who owns the property. The contract and landlord portal inherit this.",
    icon: Building2,
    fields: [
      {
        key: "landlord_id",
        label: "Landlord",
        kind: "landlord",
        impact: "Carried onto the contract and exposed in the landlord portal.",
      },
      { key: "offered_by_id", label: "Offered by", kind: "select", category: "offered_by" },
    ],
  },
  {
    id: "classification",
    title: "Classification",
    description: "What kind of home this is and how it's let.",
    icon: Tag,
    fields: [
      { key: "type_id", label: "Type", kind: "select", category: "type" },
      { key: "status_id", label: "Status", kind: "select", category: "status" },
      { key: "furnishing_id", label: "Furnishing", kind: "select", category: "furnishing" },
      { key: "flooring_id", label: "Flooring", kind: "select", category: "flooring" },
      { key: "rent_type_id", label: "Rent type", kind: "select", category: "rent_type" },
    ],
  },
  {
    id: "pricing",
    title: "Pricing",
    description: "The monthly figures shown to candidates and billed on the contract.",
    icon: Banknote,
    fields: [
      {
        key: "advertised_rent",
        label: "Rent / month",
        kind: "money",
        impact: "Shown to candidates and seeds the contract's rent.",
      },
      {
        key: "service_costs",
        label: "Service costs",
        kind: "money",
        impact: "Shown on the listing and carried into the contract.",
      },
      {
        key: "deposit",
        label: "Deposit",
        kind: "money",
        impact: "Suggested waarborgsom; carried into the contract.",
      },
    ],
  },
  {
    id: "size",
    title: "Size & terms",
    description: "Dimensions, capacity and the rental-duration limits.",
    icon: Ruler,
    fields: [
      { key: "surface_m2", label: "Surface (m²)", kind: "number" },
      { key: "roofterrace_surface_m2", label: "Roof terrace (m²)", kind: "number" },
      { key: "balcony_surface_m2", label: "Balcony (m²)", kind: "number" },
      { key: "garden_surface_m2", label: "Garden (m²)", kind: "number" },
      {
        key: "max_persons",
        label: "Max persons",
        kind: "number",
        impact: "Caps how many tenants can apply.",
      },
      {
        key: "wws_points",
        label: "WWS points",
        kind: "number",
        impact: "Drives the social vs free-sector classification.",
      },
      { key: "min_rental_months", label: "Min rental (months)", kind: "number" },
      { key: "max_rental_months", label: "Max rental (months)", kind: "number" },
    ],
  },
  {
    id: "availability",
    title: "Availability",
    description: "The key dates for publication and intake.",
    icon: CalendarClock,
    fields: [
      {
        key: "available_from",
        label: "Available from",
        kind: "date",
        impact: "Move-in date shown to candidates.",
      },
      {
        key: "online_from",
        label: "Online from",
        kind: "date",
        impact: "When the listing goes live on the channels.",
      },
      { key: "offline_from", label: "Offline from", kind: "date" },
      { key: "intake_date", label: "Intake date", kind: "date" },
    ],
  },
];

/** Numbers and money are never meaningfully negative — used for client-side validation. */
export function isNonNegativeKind(kind: FieldKind): boolean {
  return kind === "number" || kind === "money";
}
